import { User, Building2 } from "lucide-react";

/**
 * Human-readable role label. Uses tag heuristics for external_party
 * (influenser, dobavljač, itd.) — falls back to a neutral label.
 */
export function participantLabel(role, contact) {
  if (role === "assignee") return "izvršilac";
  if (role === "watcher") return "prati";
  // external_party — try to derive from contact tags
  const tags = new Set([...(contact?.tags || []), ...(contact?.custom_tags || [])]);
  const tagToLabel = {
    influencer: "influenser",
    supplier: "dobavljač",
    client: "klijent",
    photographer: "fotograf",
    freelancer: "freelancer",
    agency: "agencija",
    factory: "fabrika",
    courier: "kurir",
    partner: "partner",
  };
  for (const t of tags) {
    if (tagToLabel[t]) return tagToLabel[t];
  }
  return contact?.kind === "organization" ? "eksterna firma" : "eksterni";
}

/**
 * Read-only participants list. Shows one row per participant with an icon
 * based on kind and a role/tag label in parentheses.
 * Renders "Dodaj učesnika →" trigger when list is empty (or always when onAdd).
 */
export default function ParticipantsSection({
  participants = [],
  contactsById = {},
  onAdd,
  onRemove,
}) {
  const rows = (participants || []).map((p) => ({
    ...p,
    contact: contactsById[p.contact_id],
  }));

  return (
    <section data-testid="participants-section" className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-ink">Učesnici</div>
        {onAdd && rows.length > 0 && (
          <button
            type="button"
            onClick={onAdd}
            data-testid="participants-add-btn-inline"
            className="text-xs text-forest hover:underline"
          >
            + Dodaj
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        onAdd && (
          <button
            type="button"
            onClick={onAdd}
            data-testid="participants-add-btn"
            className="w-full text-left text-sm text-forest hover:bg-surface-secondary rounded-md border border-dashed border-line px-3 py-2"
          >
            Dodaj učesnika →
          </button>
        )
      ) : (
        <ul className="space-y-1">
          {rows.map((r, i) => (
            <li
              key={`${r.contact_id}-${r.role}-${i}`}
              data-testid={`participant-row-${r.contact_id}-${r.role}`}
              className="flex items-center gap-2 text-sm text-ink"
            >
              {(r.contact?.kind === "organization") ? (
                <Building2 className="w-4 h-4 text-ink-muted shrink-0" aria-hidden />
              ) : (
                <User className="w-4 h-4 text-ink-muted shrink-0" aria-hidden />
              )}
              <span className="flex-1 min-w-0 truncate">
                {r.contact?.name || "?"}
                <span className="text-ink-muted"> ({participantLabel(r.role, r.contact)})</span>
              </span>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(r)}
                  data-testid={`participant-remove-${r.contact_id}-${r.role}`}
                  className="text-xs text-ink-muted hover:text-terracotta px-1"
                  aria-label="Ukloni učesnika"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
